import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AddChildIcon } from 'components/atoms/Icons';
import { FC, useCallback, useState, useEffect } from 'react';
import { Crawler } from 'types/crawler';

type Props = {
  id: number;
  parentId: number;
  crawlers: Crawler[];
  visible: boolean;
  toChild: boolean;
  onClick: (id: number, parentId: number) => void;
};

export const CrawlerAddButton: FC<Props> = ({
  id,
  parentId,
  crawlers,
  visible,
  toChild,
  onClick,
}) => {
  const label = toChild ? '後続のクローラーを追加する' : 'クローラーを追加する';
  const icon = toChild ? (
    <AddChildIcon color="white" />
  ) : (
    <AddIcon color="white" />
  );

  const [searchText, setSearchText] = useState('');
  const [displayCrawlers, setDisplayCrawlers] = useState(crawlers);
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(String(event.target.value));
    },
    [],
  );

  useEffect(() => {
    const filteredCrawlers = crawlers.filter(
      (crawler) =>
        crawler.id !== id &&
        (crawler.name.toLowerCase().includes(searchText.toLowerCase()) ||
          crawler.id.toString().includes(searchText)),
    );
    setDisplayCrawlers(filteredCrawlers);
  }, [crawlers, id, searchText]);

  return (
    <Popover offset={[120, 10]} variant="darkShadow" strategy="fixed">
      <Tooltip label={label} isDisabled={!visible}>
        <Box display="inline-block">
          <PopoverTrigger>
            <IconButton
              size="xs"
              aria-label="クローラー追加"
              icon={icon}
              colorScheme="blue"
              visibility={visible ? 'visible' : 'hidden'}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent w="400px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader pr="40px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input value={searchText} onChange={handleInputChange} />
          </InputGroup>
        </PopoverHeader>
        <PopoverBody p="0" maxH="320px" overflowY="scroll">
          <List>
            {displayCrawlers.map((crawler) => (
              <ListItem
                key={crawler.id}
                display="flex"
                gap="16px"
                p="4px 16px"
                borderBottom="solid 1px #ccc"
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                _last={{ borderBottom: 'none' }}
                onClick={(event) => {
                  event.stopPropagation();
                  const to = toChild ? id : parentId;
                  onClick(crawler.id, to);
                }}
              >
                <Text fontSize="sm" color="gray.500">
                  {crawler.id}
                </Text>
                <Text fontSize="md">{crawler.name}</Text>
              </ListItem>
            ))}
          </List>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
